import { useState } from "react";
import { hotCards, type GossipCategory } from "@/mocks/gossip";

const filters = ["全部", "明星", "网红", "主播"] as const;

function HeatBar({ value }: { value: number }) {
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-[rgb(var(--ink))]/8">
      <div
        className="h-full rounded-full bg-gradient-to-r from-[rgb(var(--brand))] to-[rgb(244,63,94)]"
        style={{ width: `${(value / 10) * 100}%` }}
      ></div>
    </div>
  );
}

export default function HotGrid() {
  const [active, setActive] = useState<(typeof filters)[number]>("全部");

  const list = hotCards.filter((c) => active === "全部" || c.category === active);
  const [lead, ...rest] = list;
  const sideCards = rest.slice(0, 2);

  return (
    <section id="today" className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
      {/* 区头 */}
      <div className="mb-5 flex flex-col justify-between gap-4 md:mb-6 md:flex-row md:items-end">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold tracking-[0.25em] text-[rgb(var(--brand))]">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 animate-live rounded-full bg-[rgb(244,63,94)]"></span>
              今日瓜田
            </span>
          </div>
          <h2 className="font-display text-[24px] font-black leading-tight md:text-[30px]">
            今天，谁又翻车了？
          </h2>
          <p className="mt-1 text-[13px] text-[rgb(var(--ink-soft))]">
            按热度聚合的当日爆点，点开速览来龙去脉
          </p>
        </div>
        <div className="flex items-center gap-1 self-start rounded-full border border-[rgb(var(--ink))]/10 bg-white/60 p-1 md:self-auto">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setActive(f)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                active === f
                  ? "bg-[rgb(var(--ink))] text-[rgb(var(--paper))]"
                  : "text-[rgb(var(--ink-soft))] hover:text-[rgb(var(--brand))]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* 多卡并列首屏 */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {lead && (
          <article className="group relative flex flex-col overflow-hidden rounded-lg border border-[rgb(var(--ink))]/10 bg-white md:col-span-2 lg:col-span-2">
            <div className="relative aspect-[16/9] w-full overflow-hidden md:aspect-[21/9]">
              <img
                src={lead.image}
                alt={lead.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute left-3 top-3 rounded-full bg-[rgb(var(--brand))] px-2.5 py-1 text-[11px] font-bold text-[rgb(var(--paper))]">
                {lead.kicker}
              </span>
              <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] text-white backdrop-blur-sm">
                <i className="ri-fire-fill h-3.5 w-3.5 flex items-center justify-center text-[rgb(244,63,94)]"></i>
                爆 {lead.heat}
              </span>
            </div>
            <div className="flex flex-1 flex-col p-4 md:p-5">
              <div className="mb-2 flex items-center gap-2 text-[12px]">
                <span className="rounded-md bg-[rgb(var(--paper-deep))] px-1.5 py-0.5 font-semibold text-[rgb(var(--brand-deep))]">
                  {lead.category}
                </span>
                <span className="text-[rgb(var(--ink-soft))]">{lead.time}</span>
              </div>
              <h3 className="font-display mb-2 text-[18px] font-black leading-snug md:text-[22px]">
                {lead.title}
              </h3>
              <p className="mb-3 text-[13px] leading-relaxed text-[rgb(var(--ink-soft))]">
                {lead.summary}
              </p>
              <div className="mt-auto flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                  {lead.tags.map((t) => (
                    <span
                      key={t}
                      className="whitespace-nowrap rounded-full bg-[rgb(var(--paper))] px-2 py-0.5 text-[11px] text-[rgb(var(--ink-soft))]"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
                <span className="flex shrink-0 items-center gap-1 text-[12px] text-[rgb(var(--ink-soft))]">
                  <i className="ri-chat-3-line h-3.5 w-3.5 flex items-center justify-center"></i>
                  {lead.comments.toLocaleString()}
                </span>
              </div>
            </div>
          </article>
        )}

        {/* 右侧速读条 */}
        <div className="flex flex-col gap-4">
          {sideCards.map((c) => (
            <article
              key={c.id}
              className="group flex flex-1 flex-col justify-between overflow-hidden rounded-lg border border-[rgb(var(--ink))]/10 bg-white p-4"
            >
              <div className="mb-2 flex items-center gap-2 text-[12px]">
                <span className="rounded-md bg-[rgb(var(--paper-deep))] px-1.5 py-0.5 font-semibold text-[rgb(var(--brand-deep))]">
                  {c.category}
                </span>
                <span className="text-[rgb(var(--ink-soft))]">{c.time}</span>
                <span className="ml-auto flex items-center gap-1 text-[rgb(var(--ink-soft))]">
                  <i className="ri-eye-line h-3.5 w-3.5 flex items-center justify-center"></i>
                  {c.comments.toLocaleString()}
                </span>
              </div>
              <h3 className="font-display mb-1.5 text-[16px] font-black leading-snug group-hover:text-[rgb(var(--brand))]">
                {c.title}
              </h3>
              <p className="line-clamp-2 text-[13px] leading-relaxed text-[rgb(var(--ink-soft))]">
                {c.summary}
              </p>
            </article>
          ))}
        </div>

        {/* 其余并列小卡 */}
        {rest.slice(2).map((c) => (
          <article
            key={c.id}
            className="group overflow-hidden rounded-lg border border-[rgb(var(--ink))]/10 bg-white"
          >
            <div className="relative aspect-[3/2] w-full overflow-hidden">
              <img
                src={c.image}
                alt={c.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[11px] text-white backdrop-blur-sm">
                {c.kicker}
              </span>
            </div>
            <div className="flex flex-col p-4">
              <div className="mb-2 flex items-center justify-between text-[12px]">
                <span className="rounded-md bg-[rgb(var(--paper-deep))] px-1.5 py-0.5 font-semibold text-[rgb(var(--brand-deep))]">
                  {c.category}
                </span>
                <span className="text-[rgb(var(--ink-soft))]">{c.time}</span>
              </div>
              <h3 className="font-display mb-2 text-[15px] font-bold leading-snug group-hover:text-[rgb(var(--brand))]">
                {c.title}
              </h3>
              <div className="mb-3">
                <div className="mb-1.5 flex items-center justify-between text-[11px]">
                  <span className="text-[rgb(var(--ink-soft))]">围观热度</span>
                  <span className="flex items-center gap-1 font-semibold text-[rgb(var(--brand))]">
                    <i className="ri-fire-line h-3 w-3 flex items-center justify-center"></i>
                    {c.heat}
                  </span>
                </div>
                <HeatBar value={c.heat} />
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}